// src/components/FacebookPosts.jsx
import React, { useEffect, useMemo, useState } from "react";
import "../styles/FacebookPosts.css";
import { apiUrl } from "../lib/api";

const API = apiUrl("/api/facebook-posts");
const PAGE_LIMIT = 10;

export default function FacebookPosts() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    p.set("limit", String(PAGE_LIMIT));
    p.set("page", String(page));
    // If you want to force the Worker to bypass CF mitigations you can also append live=1,
    // but only do it if your backend expects it. Leaving it off by default:
    // p.set("live", "1");
    return p.toString();
  }, [page]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setErr("");
      setNotice("");
      try {
        const res = await fetch(`${API}?${queryString}`, {
          method: "GET",
          headers: { Accept: "application/json" },
          credentials: "omit",
          cache: "no-store",
        });

        // Handle non-2xx with readable message
        if (!res.ok) {
          const text = await res.text().catch(() => "");
          const msg =
            tryParseJSON(text)?.details ||
            tryParseJSON(text)?.error ||
            text ||
            `HTTP ${res.status}`;
          throw new Error(msg);
        }

        const json = await res.json();

        // Posts normalization: accept either { posts: [...] } or { data: [...] }
        const rawPosts = Array.isArray(json?.posts)
          ? json.posts
          : Array.isArray(json?.data)
          ? json.data
          : [];

        // Map to a consistent shape we expect to render
        const normalized = rawPosts.map((p) => ({
          id: p.id,
          message: p.message || "",
          created_time: p.created_time || p.created_time_iso || p.created || null,
          permalink_url: p.permalink_url || p.link || null,
          full_picture:
            p.full_picture ||
            // sometimes nested attachments -> pick first image if not provided
            firstPictureFromAttachments(p.attachments),
        }));

        if (!cancelled) {
          setPosts(normalized);
          setNotice(typeof json?.notice === "string" ? json.notice : "");
          setTotalPages(Math.max(1, Number(json?.paging?.applied?.total_pages || 1)));
        }
      } catch (e) {
        if (!cancelled) {
          setErr(typeof e?.message === "string" ? e.message : "Fetch failed");
          setNotice("");
          setPosts([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, queryString, reloadKey]);

  const onNext = (e) => {
    e?.preventDefault?.();
    if (page >= totalPages) return;
    setPage((n) => Math.min(totalPages, n + 1));
  };

  const onPrev = (e) => {
    e?.preventDefault?.();
    if (page <= 1) return;
    setPage((n) => Math.max(1, n - 1));
  };

  const disabledNext = loading || page >= totalPages;
  const disabledPrev = loading || page <= 1;

  return (
    <section className="fbp-wrap">
      <header className="fbp-header">
        <h2>Facebook Posts</h2>
        <div className="fbp-controls">
          <button
            className="fbp-btn"
            onClick={onPrev}
            disabled={disabledPrev}
            title={disabledPrev ? "No newer page" : "Go newer"}
          >
            ← Previous
          </button>
          <button
            className="fbp-btn"
            onClick={onNext}
            disabled={disabledNext}
            title={disabledNext ? "No older page" : "Go older"}
          >
            Next →
          </button>
          <span className="fbp-time" style={{ marginLeft: ".5rem" }}>
            Page {page} of {totalPages}
          </span>
        </div>
      </header>

      {err && (
        <div className="fbp-card" style={{ padding: "0.75rem 1rem", color: "#fca5a5" }}>
          {friendlyError(err)}
          <div style={{ marginTop: 8 }}>
            <button
              className="fbp-btn"
              onClick={() => {
                // force reload same page
                setReloadKey((n) => n + 1);
              }}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {!err && notice && (
        <div className="fbp-card" style={{ padding: "0.75rem 1rem", color: "#7a285f" }}>
          {notice}
        </div>
      )}

      {loading && (
        <div className="fbp-card" style={{ padding: "0.75rem 1rem" }}>
          Loading…
        </div>
      )}

      {!loading && !err && posts?.length === 0 && (
        <div className="fbp-card" style={{ padding: "0.75rem 1rem" }}>
          No posts on this page.
        </div>
      )}

      <div className="fbp-grid">
        {posts.map((p) => (
          <article key={p.id} className="fbp-card">
            {p.full_picture ? (
              <div className="fbp-media">
                <img src={p.full_picture} alt="" loading="lazy" />
              </div>
            ) : null}
            <div className="fbp-body">
              <div className="fbp-time">
                {p.created_time ? new Date(p.created_time).toLocaleString() : "—"}
              </div>
              <p
                className="fbp-text"
                dir={containsArabic(p.message) ? "rtl" : "auto"}
              >
                {p.message || "(No message)"}
              </p>
              {/* 🔥 REMOVED: View on Facebook Link */}
              {/*               {p.permalink_url ? (
                <a
                  className="fbp-link"
                  href={p.permalink_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  View on Facebook
                </a>
              ) : null}
              */}
            </div>
          </article>
        ))}
      </div>

      <footer className="fbp-footer">
        <button className="fbp-btn" onClick={onPrev} disabled={disabledPrev}>
          ← Previous
        </button>
        <button className="fbp-btn" onClick={onNext} disabled={disabledNext}>
          Next →
        </button>
        <nav className="fbp-page-numbers" aria-label="Facebook post pages">
          {numberedPages(page, totalPages).map((item, index) => (
            item === "ellipsis" ? (
              <span key={`ellipsis-${index}`}>…</span>
            ) : (
              <button
                type="button"
                key={item}
                className={`fbp-btn ${item === page ? "active" : ""}`}
                aria-current={item === page ? "page" : undefined}
                onClick={() => setPage(item)}
              >
                {item}
              </button>
            )
          ))}
        </nav>
      </footer>
    </section>
  );
}

/* ---------------- helpers ---------------- */

function tryParseJSON(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function firstPictureFromAttachments(attachments) {
  const list = attachments?.data || [];
  for (const a of list) {
    if (a?.media?.image?.src) return a.media.image.src;
    const sub = a?.subattachments?.data || [];
    for (const s of sub) {
      if (s?.media?.image?.src) return s.media.image.src;
    }
  }
  return null;
}

function numberedPages(currentPage, totalPages) {
  if (totalPages <= 9) return Array.from({ length: totalPages }, (_, index) => index + 1);
  const pages = new Set([1, 2, totalPages - 1, totalPages]);
  for (let page = currentPage - 2; page <= currentPage + 2; page += 1) {
    if (page > 0 && page <= totalPages) pages.add(page);
  }
  const ordered = [...pages].sort((a, b) => a - b);
  const items = [];
  ordered.forEach((page, index) => {
    if (index > 0 && page - ordered[index - 1] > 1) items.push("ellipsis");
    items.push(page);
  });
  return items;
}

function containsArabic(value) {
  return typeof value === "string" && /[\u0600-\u06ff\ufb50-\ufdff\ufe70-\ufeff]/.test(value);
}

function friendlyError(msg) {
  // Provide friendlier copy for common backend errors
  if (/Unauthorized|x-internal-key/i.test(msg)) {
    return "Unauthorized request. If this is an internal-only endpoint, ensure the correct key is supplied.";
  }
  if (/not configured|APP|token|temporarily unavailable/i.test(msg)) {
    return "Facebook posts are temporarily unavailable. Please try again shortly.";
  }
  return msg;
}
